import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {getGTDStage, Hub, isDisabledProp, PermissionObject} from 'yougile-model'
import {LinkButtonNew} from '@components/common/base/buttons'
import {OptionsItem} from '@components/common/base/dropdown-button-menu/types'
import {SelectWithDropdown} from '@components/common/base/select-with-dropdown'
import {Switcher} from '@components/common/base/switcher'
import {TablerIcons} from '@components/common/icon-tabler/icon-tabler'

const TwPermissionsRow = tw.div`
  cursor-pointer
  py-8
  px-16
  flex
  gap-16
  items-center
  justify-between
  min-h-32
`

const TwName = tw.div`
  flex-1
  text-sm-regular
  text-primary
`

const TwValue = tw.div`
  [max-width:calc(50%-8px)]
  flex-1
`

const getValue = (path: string[], object: any): any => {
  if (!path || path.length === 0) {
    return object
  }
  return getValue(path.slice(1), object[path[0]])
}

const changeValue = (path: string[], object: any, value: any): any => {
  if (path.length === 1) {
    return {...object, [path[0]]: value}
  }
  return {
    ...object,
    [path[0]]: changeValue(path.slice(1), object[path[0]], value)
  }
}

export const getIcon = (
  type: 'project' | 'board' | 'column' | 'task',
  hub: Hub
): TablerIcons => {
  if (type === 'project') {
    return 'IconBriefcase'
  } else if (type === 'board') {
    return 'IconFolder'
  } else if (type === 'column') {
    return 'IconLayoutKanban'
  } else if (type === 'task') {
    const stage = getGTDStage(hub.data.gtd)
    return stage === 'done'
      ? 'IconCustomTaskDoneFilledSmall'
      : 'IconCustomTaskDoneEmptySmall'
  } else {
    throw new Error(`Unknown type ${type}`)
  }
}

export const PermissionsSelect = (props: {
  path: string[]
  object: PermissionObject
  options: string[]
  onChange: (obj: PermissionObject) => void
}) => {
  const {object, path, options, onChange} = props
  if (isDisabledProp(object, path)) {
    return null
  }
  const value = getValue(path, object.permissions)
  const msgKey =
    'permissionsSettings' +
    Util.capitalize(object.type) +
    Util.capitalize(path.slice(-1)[0])
  const valueKey = `${msgKey}Opt${Util.capitalize(Util.kebabToCamel(value))}`
  const change = ({key}: OptionsItem) =>
    onChange(changeValue(['permissions', ...path], object, key))
  return (
    <TwPermissionsRow>
      <TwName>{Lang.findMsg(msgKey)}</TwName>
      <TwValue>
        <SelectWithDropdown
          className="w-full"
          options={options.map(key => {
            const msg = `${msgKey}Opt${Util.capitalize(Util.kebabToCamel(key))}`
            return {key, text: Lang.findMsg(msg), selected: key === valueKey}
          })}
          value={Lang.findMsg(valueKey)}
          onChange={change}
        />
      </TwValue>
    </TwPermissionsRow>
  )
}

export const PermissionsCheckbox = (props: {
  path: string[]
  object: PermissionObject
  onChange: (obj: PermissionObject) => void
}) => {
  const {object, path, onChange} = props
  if (isDisabledProp(object, path)) {
    return null
  }
  const value: boolean = getValue(path, object.permissions)
  const change = () => {
    const cur: boolean = getValue(path, object.permissions)
    onChange(changeValue(['permissions', ...path], object, !cur))
  }
  const msgKey =
    'permissionsSettings' +
    Util.capitalize(object.type) +
    Util.capitalize(path.slice(-1)[0])
  return (
    <TwPermissionsRow onClick={change}>
      <TwName>{Lang.findMsg(msgKey)}</TwName>
      <Switcher active={value} />
    </TwPermissionsRow>
  )
}

export const PermissionsHeader = ({
  allow,
  disallow,
  title,
  // isSub,
  noActions
}: {
  title: string
  isSub?: boolean
  noActions?: boolean
  allow: () => void
  disallow: () => void
}) => (
  <div className="flex items-center justify-between px-16 pt-24 pb-8">
    <span className="text-sm-semibold text-primary">{title}</span>
    {!noActions && (
      <div className="inline-flex items-center gap-4">
        <LinkButtonNew
          title={Lang.msg.permissionsDisallowAll}
          onClick={disallow}
        />
        <span className="text-sm-regular text-primary">/</span>
        <LinkButtonNew title={Lang.msg.permissionsAllowAll} onClick={allow} />
      </div>
    )}
  </div>
)

export const PermissionsTaskHeader = ({
  allow,
  disallow,
  reset,
  title,
  noActions
}: {
  title: string
  noActions?: boolean
  allow: () => void
  disallow: () => void
  reset: () => void
}) => (
  <>
    <div className="flex items-center justify-between px-16 pt-24 pb-8">
      <span className="text-sm-semibold text-primary">{title}</span>
      {!noActions && (
        <div className="inline-flex items-center gap-4">
          <LinkButtonNew
            title={Lang.msg.permissionsDisallowAll}
            onClick={disallow}
          />
          <span className="text-sm-regular text-primary">/</span>
          <LinkButtonNew title={Lang.msg.permissionsAllowAll} onClick={allow} />
        </div>
      )}
    </div>
    {!noActions && (
      <div className="flex items-center px-16 py-8">
        <LinkButtonNew
          title={Lang.msg.permissionsSettingsTaskSameReset}
          onClick={reset}
          className="ml-auto"
        />
      </div>
    )}
  </>
)
